import { Box } from "@mui/material";
import { useRouter } from "@tanstack/react-router";
import { useCallback, type SyntheticEvent } from "react";

import type { ProductCardSortBy } from "@/types/ingestedProduct";

import { DEFAULT_STEP_ID_BY_GROUP } from "@/features/Generations/steps";
import {
  pilotFlowSearchForCollection,
  type SKUStoreCollection,
} from "@/features/SKUStore/collections/skuStoreCollections";
import { SKUStoreCollectionMediaTab } from "@/features/SKUStore/components/Collections/SKUStoreCollectionMediaTab";
import { SKUStoreCollectionProductsTab } from "@/features/SKUStore/components/Collections/SKUStoreCollectionProductsTab";
import { skuStoreSubpageScrollableRootSx } from "@/features/SKUStore/skuStoreLayoutSx";
import { MuiTab } from "@/orion/MuiComponents/MuiTab";
import { MuiTabs } from "@/orion/MuiComponents/MuiTabs";

export const COLLECTION_TABS = ["products", "media"] as const;
export type CollectionTab = (typeof COLLECTION_TABS)[number];

export type SKUStoreCollectionViewProps = {
  siteId: string;
  collection: SKUStoreCollection;
  /** Active tab (URL-backed on the landing page); defaults to the product grid. */
  tab?: CollectionTab;
  onTabChange: (next: CollectionTab) => void;
  /** Product grid sort (URL-backed on the landing page). */
  sortBy?: ProductCardSortBy;
  onSortByChange: (next: ProductCardSortBy | undefined) => void;
};

/**
 * Tabbed body for a SKU Store collection (including "All Products"): an
 * overview + product grid tab and a media tab with collection-level media
 * generation.
 */
export function SKUStoreCollectionView({
  siteId,
  collection,
  tab = "products",
  onTabChange,
  sortBy,
  onSortByChange,
}: SKUStoreCollectionViewProps) {
  const router = useRouter();

  const handleTabsChange = useCallback(
    (_event: SyntheticEvent, value: number) => {
      onTabChange(COLLECTION_TABS[value]);
    },
    [onTabChange],
  );

  // The flow opens in its own tab so the collection stays put behind it.
  const handlePrepareMedia = useCallback(() => {
    const { href } = router.buildLocation({
      to: "/generations/$siteId/new/pilot/$stepId",
      params: { siteId, stepId: DEFAULT_STEP_ID_BY_GROUP.pilot },
      search: pilotFlowSearchForCollection(collection),
    });
    window.open(href, "_blank", "noopener,noreferrer");
  }, [router, siteId, collection]);

  const activeIndex = COLLECTION_TABS.indexOf(tab);

  return (
    <Box sx={skuStoreSubpageScrollableRootSx}>
      <Box
        sx={(theme) => ({
          position: "sticky",
          top: 0,
          zIndex: 1,
          bgcolor: theme.palette.background.paper,
          borderBottom: 1,
          borderColor: "divider",
        })}
      >
        <MuiTabs
          value={activeIndex}
          onChange={handleTabsChange}
          aria-label={`${collection.name} tabs`}
          sx={(theme) => ({
            minHeight: theme.spacing(6),
            paddingTop: 4,
            "& .MuiTab-root": {
              minHeight: 32,
              textTransform: "none",
              px: "20px",
              fontWeight: 400,
              "&.Mui-selected": {
                fontWeight: 600,
                color: theme.palette.primary.main,
              },
            },
          })}
        >
          <MuiTab label="All Products" id="collection-tab-products" />
          <MuiTab label="Media" id="collection-tab-media" />
        </MuiTabs>
      </Box>

      {tab === "products" ? (
        <Box role="tabpanel" aria-labelledby="collection-tab-products">
          <SKUStoreCollectionProductsTab
            siteId={siteId}
            collection={collection}
            sortBy={sortBy}
            onSortByChange={onSortByChange}
          />
        </Box>
      ) : (
        <Box role="tabpanel" aria-labelledby="collection-tab-media">
          <SKUStoreCollectionMediaTab
            siteId={siteId}
            collection={collection}
            onPrepareMedia={handlePrepareMedia}
          />
        </Box>
      )}
    </Box>
  );
}
